import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Tiers } from '../models/models';
import api from './api';
import { partageToParams } from '../utils/partageFiltre';



interface TiersState {
  tiersData: Tiers[];
  total: number;
  totalPages: number;
  currentPage: number;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  selectedTiers: Tiers | null;
}

const initialState: TiersState = {
  tiersData: [],
  total: 0,
  totalPages: 0,
  currentPage: 0,
  loading: false,
  error: null,
  successMessage: null,
  selectedTiers: null,
};

/**
 * Récupère les tiers paginés avec filtres optionnels.
 * Tous les filtres (type, nom, email, telephone, tiers_id) sont optionnels —
 * absents = pas de contrainte côté SP. Aligné sur sp_select_tiersByFilters.
 */
export interface TiersFilters {
  type?:         string | null;
  nom?:          string | null;
  email?:        string | null;
  telephone?:    string | null;
  tiers_id?:     number | null;
  categorie_id?: number | null;
  partage?:      number[] | null;   // filtre « Partage » (0132) — voir SelectFiltrePartage
  limit:         number;
  offset:        number;
}

export const fetchTiers = createAsyncThunk<
  { data: Tiers[]; total: number; totalPages: number; currentPage: number },
  TiersFilters,
  { rejectValue: string }
>(
  'tiers/fetchTiers',
  async (filters, thunkAPI) => {
    try {
      // Envoie uniquement les filtres non-nuls (axios ignore les undefined)
      const params: Record<string, any> = {
        limit:  filters.limit,
        offset: filters.offset,
      };
      if (filters.type)      params.type      = filters.type;
      if (filters.nom)       params.nom       = filters.nom;
      if (filters.email)     params.email     = filters.email;
      if (filters.telephone)    params.telephone    = filters.telephone;
      if (filters.tiers_id)     params.tiers_id     = filters.tiers_id;
      // != null : 0 = « sans catégorie » (migration 0133) doit être transmis
      if (filters.categorie_id != null) params.categorie_id = filters.categorie_id;
      Object.assign(params, partageToParams(filters.partage));

      const response = await api.get('/tiers/getAll', { params });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.error || error.response?.data?.message || 'Échec du chargement des données.'
      );
    }
  }
);

export const createTiers = createAsyncThunk<
  Tiers,
  Tiers,
  { rejectValue: string }
>(
  'tiers/createTiers',
  async (tiersData, thunkAPI) => {
    try {
      const response = await api.post('/tiers/createOrUpdate', tiersData);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Échec de la création.');
    }
  }
);
// Import Excel — tout ou rien ; le type de tiers vient de l'écran, pas du fichier
export const importTiersExcel = createAsyncThunk<
  { success: number; errors: Array<{ row: number; message: string }> },
  { lignes: Record<string, any>[]; type: string },
  { rejectValue: string }
>('tiers/importExcel', async (payload, thunkAPI) => {
  try {
    const response = await api.post('/tiers/import', payload);
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.error || error.response?.data?.message || 'Erreur lors de l\'import'
    );
  }
});

// ✅ Supprimer un tiers
export const deleteTiers = createAsyncThunk<
  number, // Retourne l'ID supprimé
  number, // Paramètre : ID du tiers à supprimer
  { rejectValue: string }
>(
  'tiers/deleteTiers',
  async (tiersId, thunkAPI) => {
    try {
      await api.delete(`/tiers`, { params: { id: tiersId } });
      return tiersId;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Échec de la suppression.');
    }
  }
);


const tiersSlice = createSlice({
  name: 'tiers',
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setSelectedTiers: (state, action: PayloadAction<Tiers | null>) => {
      state.selectedTiers = action.payload;
    },
    clearTiersData:(state)=>{
      state.tiersData=[];
    },
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // 🔹 Fetch Tiers (unifié — type/nom/email/telephone/tiers_id optionnels)
      .addCase(fetchTiers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTiers.fulfilled, (state, action) => {
        state.loading = false;
        state.tiersData = action.payload.data;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchTiers.rejected, (state, action) => {
        if (action.meta.aborted) return;   // remplacé par un fetch plus récent (page/filtre)
        state.loading = false;
        state.error = action.payload as string;
      })
      // 🔹 Create Tiers
      .addCase(createTiers.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createTiers.fulfilled, (state, action: PayloadAction<Tiers>) => {
        state.loading = false;
        state.successMessage = 'Création réussie !';
       // state.data = [action.payload, ...state.data];
      })
      .addCase(createTiers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // 🔹 Delete Tiers
      .addCase(deleteTiers.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(deleteTiers.fulfilled, (state, action: PayloadAction<number>) => {
        state.loading = false;
        state.successMessage = 'Suppression réussie !';
        state.tiersData = state.tiersData.filter((t) => t.id !== action.payload);

        if (state.selectedTiers?.id === action.payload) {
          state.selectedTiers = null;
        }
      })
      .addCase(deleteTiers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setCurrentPage, setSelectedTiers, clearMessages,clearTiersData } = tiersSlice.actions;
export default tiersSlice.reducer;
